import { Microfrontend } from '@cian/mf-react';
import { IMicrofrontendManifest } from '@cian/mf-registry/shared';
import * as React from 'react';

import { AdBannerPlaceholder } from './components/AdBannerPlaceholder';
import { useAdBannerCommonParameters, useAdBannerMicrofrontend, useAdBannerMicrofrontendEvents } from './hooks';
import { TAdBannerMicrofrontendParameters, TAdBannerPlaceParameters } from './types';

interface IAdBannerMicrofrontendContainerProps {
  /** Манифест микрофронтенда ad-banner-frontend (один на все места размещения) */
  manifest: IMicrofrontendManifest;
  /** Параметры конкретного места */
  parameters: TAdBannerPlaceParameters;
  /** Колбэк при заглушке (нет рекламы) */
  onStub?: () => void;
  /** Колбэк при ошибке загрузки/рендера */
  onError?: () => void;
  /** Колбэк при закрытии баннера */
  onClose?: () => void;
}

/**
 * Generic-контейнер микрофронтенда баннеров: ничего не знает о конкретном месте.
 *
 * Сам резолвит общие, региональные параметры (`puids`, `regionId`, `placeId`), резервирует
 * место плейсхолдером, монтирует `<Microfrontend>` только на клиенте и мёржит параметры
 * `{ ...common, ...place }` (common — база, place перекрывает).
 */
export const AdBannerMicrofrontendContainer: React.FC<IAdBannerMicrofrontendContainerProps> = ({
  manifest,
  parameters,
  onStub,
  onError,
  onClose,
}) => {
  const { microfrontendRef, isReady } = useAdBannerMicrofrontend();
  const commonParameters = useAdBannerCommonParameters();

  useAdBannerMicrofrontendEvents({ placeId: commonParameters.placeId, isReady, onStub, onError, onClose });

  const { microfrontendName, imageVersion, runtimeName } = manifest;

  // Мемоизируем итоговые параметры: common — база, place-параметры перекрывают её.
  const mergedParameters = React.useMemo(
    (): TAdBannerMicrofrontendParameters => ({ ...commonParameters, ...parameters }),
    [parameters, commonParameters],
  );

  return (
    <AdBannerPlaceholder sizes={parameters.sizes} testId={`AdBannerMicrofrontend-${parameters.place}`}>
      {isReady && (
        <Microfrontend
          ref={microfrontendRef}
          name={microfrontendName}
          image={imageVersion}
          runtime={runtimeName}
          parameters={mergedParameters}
        />
      )}
    </AdBannerPlaceholder>
  );
};
